<template>
  <div class="app-container">
    <div v-if="!hasPermission" class="no-permission">
      <i class="el-icon-warning-outline"></i>
      <h2>您没有权限访问此页面</h2>
      <p>请联系系统管理员获取权限</p>
    </div>

    <template v-else>
      <!-- 搜索区域 -->
      <el-card class="search-card" shadow="never">
        <el-form
          :model="filters"
          ref="queryForm"
          size="small"
          :inline="true"
          label-width="80px"
          @submit.native.prevent
        >
          <el-form-item label="客户名称" prop="customerName">
            <el-input
              v-model="filters.customerName"
              placeholder="请输入客户名称"
              clearable
              style="width: 200px;"
              @keyup.enter.native="handleSearch"
              @clear="handleSearch"
            />
          </el-form-item>
          <el-form-item label="客户类型" prop="customerType">
            <el-select
              v-model="filters.customerType"
              placeholder="请选择客户类型"
              clearable
              style="width: 150px;"
              @change="handleSearch"
              @clear="handleSearch"
            >
              <el-option label="公司" :value="CUSTOMER_TYPE.COMPANY" />
              <el-option label="个人" :value="CUSTOMER_TYPE.INDIVIDUAL" />
            </el-select>
          </el-form-item>
          <el-form-item label="订单状态" prop="orderStatus">
            <el-select
              v-model="filters.orderStatus"
              placeholder="请选择订单状态"
              clearable
              style="width: 150px;"
              @change="handleSearch"
              @clear="handleSearch"
            >
              <el-option label="待审核" :value="ORDER_STATUS.PENDING" />
              <el-option label="审核失败" :value="ORDER_STATUS.FAIL" />
              <el-option label="已完成" :value="ORDER_STATUS.COMPLETED" />
              <el-option label="已作废" :value="ORDER_STATUS.CANCELLED" />
              <el-option label="待补差价" :value="ORDER_STATUS.PENDING_BALANCE" />
            </el-select>
          </el-form-item>
          <el-form-item label="创建人" prop="createdBy">
            <el-select
              v-model="filters.createdBy"
              placeholder="请输入或选择创建人"
              clearable
              filterable
              :filter-method="filterCreators"
              style="width: 150px;"
              :loading="loadingCreators"
              @change="handleSearch"
              @clear="handleSearch"
            >
              <el-option
                v-for="creator in filteredCreatorList"
                :key="creator.username"
                :label="creator.username"
                :value="creator.username"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" size="mini" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 列表区域 -->
      <el-card class="table-card" shadow="never">
        <div class="table-toolbar">
          <div class="toolbar-left"></div>
          <div class="toolbar-right">
            <el-tooltip content="刷新" placement="top">
              <el-button
                circle
                icon="el-icon-refresh"
                size="mini"
                @click="handleSearch"
              />
            </el-tooltip>
          </div>
        </div>

        <el-table
          :data="allOrders"
          border
          stripe
          size="small"
          style="width: 100%;"
          :header-cell-style="headerCellStyle"
          :row-class-name="tableRowClassName"
        >
          <el-table-column prop="orderId" label="订单ID" min-width="120" show-overflow-tooltip />
           <el-table-column label="招商" width="100" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.createdBy || '系统' }}
            </template>
          </el-table-column>
          <el-table-column prop="customerName" label="客户名称" min-width="120" show-overflow-tooltip />
          <el-table-column label="产品" min-width="110" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.productName || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="客户类型" width="90" align="center">
            <template slot-scope="scope">
              {{ getTypeLabel(scope.row.type) }}
            </template>
          </el-table-column>
          <el-table-column label="款项类型" width="90" align="center">
            <template slot-scope="scope">
              {{ getPaymentTypeLabel(scope.row.paymentType) }}
            </template>
          </el-table-column>
          <el-table-column label="总金额" width="110" align="center">
            <template slot-scope="scope">
              <span class="amount-text">¥{{ scope.row.amount }}</span>
            </template>
          </el-table-column>
          <el-table-column label="定金" width="100" align="center">
            <template slot-scope="scope">
              <span
                v-if="Number(scope.row.paymentType) === PAYMENT_TYPE.DEPOSIT"
                class="deposit-text"
              >¥{{ scope.row.depositAmount || 0 }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="数量" width="80" align="center">
            <template slot-scope="scope">
              {{ scope.row.orderQuantity || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="订单日期" width="120" align="center">
            <template slot-scope="scope">
              {{ formatDate(scope.row.orderDate) }}
            </template>
          </el-table-column>
          <el-table-column label="备注" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.remark || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusTagType(scope.row.status)" size="mini">
                {{ getStatusLabel(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="110" align="center" fixed="right" class-name="small-padding fixed-width">
            <template slot-scope="scope">
              <el-button
                type="text"
                icon="el-icon-view"
                size="mini"
                @click="viewOrderDetails(scope.row)"
              >详情</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrap" v-if="total > 0">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            :current-page="currentPage"
            :page-size="pageSize"
            :page-sizes="[10, 20, 50]"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </el-card>

      <!-- 订单详情弹窗 -->
      <el-dialog
        title="订单详情"
        :visible.sync="showOrderDetails"
        width="720px"
        append-to-body
        :close-on-click-modal="false"
        @closed="closeOrderDetails"
      >
        <div v-if="selectedOrder" class="detail-body">
          <el-descriptions :column="2" size="small" border>
            <el-descriptions-item label="订单ID">{{ selectedOrder.orderId }}</el-descriptions-item>
            <el-descriptions-item label="客户名称">{{ selectedOrder.customerName }}</el-descriptions-item>
            <el-descriptions-item label="产品">{{ selectedOrder.productName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="招商">{{ selectedOrder.createdBy || '系统' }}</el-descriptions-item>
            <el-descriptions-item label="客户类型">{{ getTypeLabel(selectedOrder.type) }}</el-descriptions-item>
            <el-descriptions-item label="款项类型">{{ getPaymentTypeLabel(selectedOrder.paymentType) }}</el-descriptions-item>
            <el-descriptions-item label="总金额">
              <span class="amount-text">¥{{ selectedOrder.amount }}</span>
            </el-descriptions-item>
            <el-descriptions-item
              v-if="Number(selectedOrder.paymentType) === PAYMENT_TYPE.DEPOSIT"
              label="定金金额"
            ><span class="deposit-text">¥{{ selectedOrder.depositAmount || 0 }}</span></el-descriptions-item>
            <el-descriptions-item label="数量">{{ selectedOrder.orderQuantity || '未填写' }}</el-descriptions-item>
            <el-descriptions-item label="订单日期">{{ formatDate(selectedOrder.orderDate) }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag :type="getStatusTagType(selectedOrder.status)" size="mini">
                {{ getStatusLabel(selectedOrder.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item v-if="selectedOrder.remark" label="备注" :span="2">
              <div class="remark-content">{{ selectedOrder.remark }}</div>
            </el-descriptions-item>
          </el-descriptions>

          <!-- 按截图类型展示：2全款 / 0定金 / 1补差价；旧全款单兼容 orderScreenshotUrl -->
          <div
            class="screenshots-section"
            v-if="Number(selectedOrder.paymentType) === PAYMENT_TYPE.FULL && getFullScreenshotUrls().length"
          >
            <div class="section-title">全款截图</div>
            <div class="screenshots-grid">
              <img
                v-for="(url, index) in getFullScreenshotUrls()"
                :key="'full-' + index"
                :src="url"
                alt="全款截图"
                class="screenshot-thumb"
                @click="openImageViewer(getFullScreenshotUrls(), index, '全款截图')"
                @error="handleImageError($event, '全款截图')"
              >
            </div>
          </div>

          <div
            class="screenshots-section"
            v-if="Number(selectedOrder.paymentType) === PAYMENT_TYPE.DEPOSIT && getScreenshotsByType(SCREENSHOT_TYPE.DEPOSIT).length"
          >
            <div class="section-title">定金截图</div>
            <div class="screenshots-grid">
              <img
                v-for="(url, index) in getScreenshotsByType(SCREENSHOT_TYPE.DEPOSIT)"
                :key="'deposit-' + index"
                :src="url"
                alt="定金截图"
                class="screenshot-thumb"
                @click="openImageViewer(getScreenshotsByType(SCREENSHOT_TYPE.DEPOSIT), index, '定金截图')"
                @error="handleImageError($event, '定金截图')"
              >
            </div>
          </div>

          <div
            class="screenshots-section"
            v-if="Number(selectedOrder.paymentType) === PAYMENT_TYPE.DEPOSIT && getScreenshotsByType(SCREENSHOT_TYPE.BALANCE).length"
          >
            <div class="section-title">补差价截图</div>
            <div class="screenshots-grid">
              <img
                v-for="(url, index) in getScreenshotsByType(SCREENSHOT_TYPE.BALANCE)"
                :key="'balance-' + index"
                :src="url"
                alt="补差价截图"
                class="screenshot-thumb"
                @click="openImageViewer(getScreenshotsByType(SCREENSHOT_TYPE.BALANCE), index, '补差价截图')"
                @error="handleImageError($event, '补差价截图')"
              >
            </div>
          </div>

          <div class="screenshots-section" v-if="Number(selectedOrder.status) === ORDER_STATUS.PENDING_BALANCE">
            <div class="section-title">上传补差价截图</div>
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="uploadBalanceScreenshot"
              accept="image/*"
            >
              <el-button size="mini" type="primary" icon="el-icon-upload2" :loading="uploadingBalance">选择并上传</el-button>
            </el-upload>
          </div>
        </div>

        <div slot="footer" class="dialog-footer" v-if="selectedOrder">
          <el-button
            v-if="Number(selectedOrder.status) === ORDER_STATUS.PENDING"
            type="success"
            size="small"
            @click="approveOrder(selectedOrder.orderId)"
          >{{ approveButtonText }}</el-button>
          <el-button
            v-if="Number(selectedOrder.status) === ORDER_STATUS.PENDING_BALANCE"
            type="success"
            size="small"
            @click="approveOrder(selectedOrder.orderId)"
          >确认补差价完成</el-button>
          <el-button
            v-if="Number(selectedOrder.status) === ORDER_STATUS.PENDING || Number(selectedOrder.status) === ORDER_STATUS.PENDING_BALANCE"
            type="danger"
            size="small"
            @click="rejectOrder(selectedOrder.orderId)"
          >审核失败</el-button>
          <el-button size="small" @click="showOrderDetails = false">关 闭</el-button>
        </div>
      </el-dialog>

      <!-- 图片查看器组件 -->
      <ImageViewer
        :visible="imageViewer.visible"
        :images="imageViewer.images"
        :initial-index="imageViewer.initialIndex"
        :title="imageViewer.title"
        @close="closeImageViewer"
        @indexChange="handleImageIndexChange"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import request from '@/utils/request'
import ImageViewer from '@/components/ImageViewer.vue'
import {
  CUSTOMER_TYPE,
  PAYMENT_TYPE,
  ORDER_STATUS,
  SCREENSHOT_TYPE,
  getCustomerTypeLabel,
  getPaymentTypeLabel,
  getOrderStatusLabel,
  getOrderStatusTagType
} from '@/utils/enums'

export default {
  name: 'AdminDashboard',
  components: {
    ImageViewer
  },
  data() {
    return {
      CUSTOMER_TYPE,
      PAYMENT_TYPE,
      ORDER_STATUS,
      SCREENSHOT_TYPE,
      selectedOrder: null,
      showOrderDetails: false,
      orderDetails: {},
      currentPage: 1,
      pageSize: 10,
      total: 0,
      filters: {
        customerName: '',
        customerType: '',
        orderStatus: '',
        createdBy: ''
      },
      creatorList: [],
      filteredCreatorList: [],
      loadingCreators: false,
      creatorSearchText: '',
      uploadingBalance: false,
      imageViewer: {
        visible: false,
        images: [],
        initialIndex: 0,
        title: ''
      },
      headerCellStyle: {
        background: '#f8f8f9',
        color: '#515a6e',
        fontWeight: '600'
      }
    }
  },
  computed: {
    ...mapGetters({
      allOrders: 'orders/allOrders',
      userInfo: 'user/userInfo'
    }),
    hasPermission() {
      try {
        const permission = localStorage.getItem('user-permission')
        return permission === '1'
      } catch (error) {
        return false
      }
    },
    approveButtonText() {
      if (!this.selectedOrder) return '审核通过'
      const isDeposit = Number(this.selectedOrder.paymentType) === PAYMENT_TYPE.DEPOSIT
      if (!isDeposit) return '审核通过'
      const hasBalance = this.getScreenshotsByType(SCREENSHOT_TYPE.BALANCE).length > 0
      return hasBalance ? '补差价审核通过' : '定金审核通过'
    }
  },
  async created() {
    if (!this.hasPermission) {
      return
    }

    await this.fetchCreatorList()

    try {
      const pageData = await this.fetchAllOrders({
        page: this.currentPage,
        size: this.pageSize,
        filters: this.filters
      })
      if (pageData) {
        this.total = pageData.total || 0
        if (typeof pageData.current === 'number') {
          this.currentPage = pageData.current
        }
        if (typeof pageData.size === 'number') {
          this.pageSize = pageData.size
        }
      }
    } catch (error) {
      this.$message.error('获取订单列表失败')
    }
  },
  methods: {
    ...mapActions({
      fetchAllOrders: 'orders/fetchAllOrders'
    }),
    getTypeLabel(type) {
      return getCustomerTypeLabel(type)
    },
    getStatusLabel(status) {
      return getOrderStatusLabel(status)
    },
    getPaymentTypeLabel(type) {
      return getPaymentTypeLabel(type)
    },
    getStatusTagType(status) {
      return getOrderStatusTagType(status)
    },
    tableRowClassName({ row }) {
      const status = Number(row.status)
      if (status === ORDER_STATUS.PENDING) {
        return 'pending-row'
      }
      if (status === ORDER_STATUS.PENDING_BALANCE) {
        return 'pending-balance-row'
      }
      return ''
    },
    async handlePageChange(page) {
      this.currentPage = page
      const pageData = await this.fetchAllOrders({
        page: this.currentPage,
        size: this.pageSize,
        filters: this.filters
      })
      if (pageData) {
        this.total = pageData.total || 0
      }
    },
    async handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      const pageData = await this.fetchAllOrders({
        page: this.currentPage,
        size: this.pageSize,
        filters: this.filters
      })
      if (pageData) {
        this.total = pageData.total || 0
      }
    },
    async handleSearch() {
      this.currentPage = 1
      try {
        const pageData = await this.fetchAllOrders({
          page: this.currentPage,
          size: this.pageSize,
          filters: this.filters
        })
        if (pageData) {
          this.total = pageData.total || 0
        }
      } catch (error) {
        this.$message.error('搜索失败')
      }
    },
    async handleReset() {
      this.filters = {
        customerName: '',
        customerType: '',
        orderStatus: '',
        createdBy: ''
      }
      this.currentPage = 1
      try {
        const pageData = await this.fetchAllOrders({
          page: this.currentPage,
          size: this.pageSize,
          filters: this.filters
        })
        if (pageData) {
          this.total = pageData.total || 0
        }
      } catch (error) {
        this.$message.error('重置失败')
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    async viewOrderDetails(order) {
      this.selectedOrder = { ...order, screenshots: [] }
      this.showOrderDetails = true

      try {
        const response = await request.get(`/api/orderDetail?orderId=${order.orderId}`)
        const processedData = this.processOrderDetailData(response || {})
        this.selectedOrder = {
          ...this.selectedOrder,
          ...processedData
        }
      } catch (error) {
        this.$message.warning('获取订单详情失败，显示基础信息')
      }
    },
    closeOrderDetails() {
      this.selectedOrder = null
      this.showOrderDetails = false
    },
    processOrderDetailData(data) {
      const detail = data.orderDetail || data
      const screenshots = Array.isArray(data.screenshots) ? data.screenshots : []
      return {
        orderScreenshotUrl: detail && detail.orderScreenshotUrl
          ? String(detail.orderScreenshotUrl).replace(/[`']/g, '').trim()
          : '',
        screenshots: screenshots.map(item => ({
          ...item,
          screenshotUrl: item.screenshotUrl
            ? String(item.screenshotUrl).replace(/[`']/g, '').trim()
            : ''
        })).filter(item => item.screenshotUrl)
      }
    },
    getScreenshotsByType(type) {
      if (!this.selectedOrder || !Array.isArray(this.selectedOrder.screenshots)) return []
      return this.selectedOrder.screenshots
        .filter(item => Number(item.screenshotType) === Number(type) && item.screenshotUrl)
        .map(item => item.screenshotUrl)
    },
    getFullScreenshotUrls() {
      const typed = this.getScreenshotsByType(SCREENSHOT_TYPE.FULL)
      if (typed.length) return typed
      // 兼容旧全款单：仅存在 order_detail.order_screenshot_url
      const legacy = this.selectedOrder && this.selectedOrder.orderScreenshotUrl
        ? String(this.selectedOrder.orderScreenshotUrl).trim()
        : ''
      return legacy ? [legacy] : []
    },
    async uploadBalanceScreenshot(option) {
      const file = option.file
      if (!file || !this.selectedOrder) return
      this.uploadingBalance = true
      try {
        const formData = new FormData()
        formData.append('image', file)
        const url = await request.post('/api/common/upload/screenshot', formData)
        await request.post('/api/orderScreenshot', {
          orderId: this.selectedOrder.orderId,
          screenshotType: SCREENSHOT_TYPE.BALANCE,
          screenshotUrl: url
        })
        this.$message.success('补差价截图上传成功，订单已进入待审核')
        // 状态已变为待审核，刷新列表与详情
        this.selectedOrder = {
          ...this.selectedOrder,
          status: ORDER_STATUS.PENDING
        }
        await this.viewOrderDetails(this.selectedOrder)
        try {
          await this.fetchAllOrders({
            page: this.currentPage,
            size: this.pageSize,
            filters: this.filters
          })
        } catch (e) { /* ignore refresh error */ }
      } catch (error) {
        this.$message.error((error && error.message) || '补差价截图上传失败')
      } finally {
        this.uploadingBalance = false
      }
    },
    async approveOrder(orderId) {
      try {
        const msg = await request.put(`/api/order?orderStatus=${ORDER_STATUS.COMPLETED}&orderId=${orderId}`)
        this.$message.success(typeof msg === 'string' ? msg : '操作成功')
        this.closeOrderDetails()
        try {
          await this.fetchAllOrders({
            page: this.currentPage,
            size: this.pageSize,
            filters: this.filters
          })
        } catch (refreshError) {
          this.$message.warning('操作成功，但数据刷新失败，请手动刷新页面')
        }
      } catch (error) {
        this.$message.error((error && error.message) || '审核失败，请重试')
      }
    },
    async rejectOrder(orderId) {
      try {
        await request.put(`/api/order?orderStatus=${ORDER_STATUS.FAIL}&orderId=${orderId}`)
        this.$message.success('订单已审核失败！')
        this.closeOrderDetails()
        try {
          await this.fetchAllOrders({
            page: this.currentPage,
            size: this.pageSize,
            filters: this.filters
          })
        } catch (refreshError) {
          this.$message.warning('订单已审核失败，但数据刷新失败，请手动刷新页面')
        }
      } catch (error) {
        this.$message.error((error && error.message) || '操作失败，请重试')
      }
    },
    handleImageError(event, type) {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjgwIiB2aWV3Qm94PSIwIDAgMTIwIDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iODAiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSI2MCIgeT0iNDAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWbvueJh+WKoOi9vTwvdGV4dD48L3N2Zz4='
      event.target.alt = `${type}加载失败`
    },
    async fetchCreatorList() {
      this.loadingCreators = true
      try {
        const response = await request.get('/api/account/list')
        let list = []
        if (Array.isArray(response)) {
          list = response
        } else if (response && Array.isArray(response.records)) {
          list = response.records
        } else if (response && Array.isArray(response.data)) {
          list = response.data
        }
        this.creatorList = list
        this.filteredCreatorList = list
      } catch (error) {
        this.$message.error('获取创建人列表失败')
        this.creatorList = []
        this.filteredCreatorList = []
      } finally {
        this.loadingCreators = false
      }
    },
    filterCreators(query) {
      this.creatorSearchText = query || ''
      if (!query) {
        this.filteredCreatorList = this.creatorList
      } else {
        const searchText = query.toLowerCase()
        this.filteredCreatorList = this.creatorList.filter(creator => {
          const username = creator.username || ''
          return username.toLowerCase().includes(searchText)
        })
      }
    },
    openImageViewer(images, initialIndex = 0, title = '') {
      this.imageViewer = {
        visible: true,
        images: images || [],
        initialIndex: Math.max(0, initialIndex),
        title: title
      }
    },
    closeImageViewer() {
      this.imageViewer.visible = false
    },
    handleImageIndexChange(newIndex) {
      this.imageViewer.initialIndex = newIndex
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.no-permission {
  background: #FFFFFF;
  border-radius: 8px;
  border: 1px solid #E7E3DA;
  padding: 60px 24px;
  text-align: center;
  color: #6B7077;
}

.no-permission i {
  font-size: 48px;
  color: #3C5A78;
  margin-bottom: 12px;
}

.no-permission h2 {
  margin: 8px 0;
  font-size: 18px;
  color: #1E2227;
  font-weight: 600;
}

.search-card {
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #E7E3DA;
}

.search-card >>> .el-card__body {
  padding: 20px 20px 8px;
}

.table-card {
  border-radius: 8px;
  border: 1px solid #E7E3DA;
}

.table-card >>> .el-card__body {
  padding: 20px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amount-text {
  color: #3C5A78;
  font-weight: 600;
}

.deposit-text {
  color: #409eff;
  font-weight: 600;
}

.danger-text {
  color: #f56c6c !important;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}

.detail-body {
  max-height: 65vh;
  overflow-y: auto;
}

.section-title {
  margin: 16px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.screenshots-section {
  margin-top: 8px;
}

.screenshots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.screenshot-thumb {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: transform 0.2s;
}

.screenshot-thumb:hover {
  transform: scale(1.03);
}

.remark-content {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  color: #606266;
}

/* 覆盖 stripe 斑马纹，避免偶数行状态底色丢失 */
>>> .el-table .pending-row > td {
  background: #fdf6ec !important;
}

>>> .el-table .pending-row:hover > td {
  background: #faecd8 !important;
}

>>> .el-table .pending-balance-row > td {
  background: #ecf5ff !important;
}

>>> .el-table .pending-balance-row:hover > td {
  background: #d9ecff !important;
}
</style>
